import { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import {
  MobileApprovalList,
  MobileFilterSheet,
  MobileSelectScreen,
} from '@shared/components/organisms/mobile';

import { cleanParams } from '@shared/utils/cleanParams';
import { getDefaultDateRange } from '@shared/utils/formatDate';

import searchIcon from '@shared/assets/images/search_Icon_mobile.svg';
import searchFilterIcon from '@shared/assets/images/search-filter_Icon.svg';
import chevronDownIcon from '@shared/assets/images/chevron_down_icon.svg';
import closeIcon from '@shared/assets/images/close_icon.svg';

import styles from './MobileDrafterContent.module.scss';

/**
 * 모바일 참조함 페이지 콘텐츠
 * - 헤더 아래 바로 콘텐츠 표시 (탭 없음)
 * - 검색 바, 정렬, 리스트 포함
 * - 필터 바텀시트 지원
 */
function MobileReferenceContent({
  onItemClick,
  formOptions,
  teamOptions,
  onComplete,
  receiveSection,
}) {
  const [searchValue, setSearchValue] = useState('');

  // 실제 검색 값
  const [appliedSearchValue, setAppliedSearchValue] = useState('');
  const [sortValue, setSortValue] = useState('incompleteLatest');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortDropdownRef = useRef(null);

  // 정렬 드롭다운: 외부 터치/클릭 시 닫기 (모바일 포함)
  useEffect(function () {
    function handlePointerOutside(event) {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setIsSortOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerOutside);
    document.addEventListener('touchstart', handlePointerOutside, { passive: true });
    return function () {
      document.removeEventListener('mousedown', handlePointerOutside);
      document.removeEventListener('touchstart', handlePointerOutside);
    };
  }, []);

  // 전체선택
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const { tableData: data, isLoading, isFetching, setSearchParams } = receiveSection;

  //전체선택 핸들러
  function handleToggleAll() {
    if (isAllSelected) {
      setSelectedIds([]);
      return;
    }

    const allIds = filteredAndSortedData.map((item) => item.idx).filter(Boolean);

    setSelectedIds(allIds);
  }

  // 필터 관련 상태
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [selectField, setSelectField] = useState(null);

  // 기본 날짜 범위
  const defaultRange = getDefaultDateRange();

  // 필터 값 상태
  const [filterValues, setFilterValues] = useState({
    date1: defaultRange.start,
    date2: defaultRange.end,
    docType: '',
    team: '',
    title: '',
    name: '',
  });

  // 필터 필드 정의
  const filterFields = useMemo(
    function () {
      return [
        {
          key: 'dateRange',
          type: 'dateRange',
          label: '조회기간',
          startKey: 'date1',
          endKey: 'date2',
          startPlaceholder: defaultRange.start,
          endPlaceholder: defaultRange.end,
        },
        {
          key: 'docType',
          type: 'select',
          label: '문서유형',
          options: formOptions || [{ label: '전체', value: '' }],
        },
        {
          key: 'team',
          type: 'select',
          label: '기안부서',
          options: teamOptions || [{ label: '전체', value: '' }],
        },
        {
          key: 'title',
          type: 'input',
          label: '문서제목',
          placeholder: '제목을 입력해 주세요.',
        },
        {
          key: 'name',
          type: 'input',
          label: '기안자',
          placeholder: '홍길동',
        },
      ];
    },
    [formOptions, teamOptions, defaultRange],
  );

  // 필터링 및 정렬된 데이터
  // - 서버(API)가 applySearchParams로 이미 필터 적용됨 → data는 필터 조건에 맞는 목록
  // - appliedSearchValue만 클라이언트에서 추가 필터 (검색창 입력값)
  const filteredAndSortedData = useMemo(
    function () {
      if (!data || data.length === 0) {
        return [];
      }

      let filtered = data;

      // 검색창 제목 검색만 클라이언트 필터 (서버 필터는 applySearchParams에서 처리)
      if (appliedSearchValue) {
        filtered = filtered.filter(function (item) {
          const title = item.title || '';
          return title.toLowerCase().includes(appliedSearchValue.toLowerCase());
        });
      }

      // 정렬 적용: 미완료 최신순(exeState 0 먼저 + 기안일 내림차순) / 완료 최신순(exeState 1 먼저 + 기안일 내림차순)
      return filtered.sort(function (a, b) {
        const exeA = Number(a.exeState);
        const exeB = Number(b.exeState);
        const dateA = a.date || '';
        const dateB = b.date || '';

        if (sortValue === 'incompleteLatest') {
          // 미완료(0) 먼저, 동일 시 기안일 최신순
          if (exeA !== exeB) return exeA - exeB;
          return dateB.localeCompare(dateA);
        }
        if (sortValue === 'completeLatest') {
          // 완료(1) 먼저, 동일 시 기안일 최신순
          if (exeA !== exeB) return exeB - exeA;
          return dateB.localeCompare(dateA);
        }
        return dateB.localeCompare(dateA);
      });
    },
    [data, appliedSearchValue, sortValue],
  );

  // 정렬 옵션 (모바일 참조함: 미완료/완료 기준)
  const sortOptions = [
    { value: 'incompleteLatest', label: '미완료 최신순' },
    { value: 'completeLatest', label: '완료 최신순' },
  ];

  // 현재 선택된 정렬 라벨
  const selectedSortLabel =
    sortOptions.find((opt) => opt.value === sortValue)?.label || '미완료 최신순';

  function handleSortSelect(value) {
    setSortValue(value);
    setIsSortOpen(false);
  }

  function handleKeyDown(event) {
    if (event.key === 'Enter') {
      // 검색 실행 (이미 useMemo에서 처리됨)
      // 엔터가 눌렷을때만 실행되게 변경
      setAppliedSearchValue(searchValue);
    }
  }

  // 필터 버튼 클릭
  function handleFilterClick() {
    setIsFilterOpen(true);
  }

  // 필터 값 변경
  function handleFilterChange(newValues) {
    setFilterValues(newValues);
  }

  // 필터 초기화
  function handleFilterReset() {
    setFilterValues({
      date1: defaultRange.start,
      date2: defaultRange.end,
      docType: '',
      team: '',
      title: '',
      name: '',
    });
  }

  // 셀렉트 필드 클릭 (풀스크린 선택 화면 열기)
  function handleSelectFieldClick(field, currentValue) {
    setSelectField({
      ...field,
      currentValue: currentValue,
    });
    setIsSelectOpen(true);
  }

  // 셀렉트 값 변경
  function handleSelectChange(value) {
    if (selectField) {
      setFilterValues(function (prev) {
        return { ...prev, [selectField.key]: value };
      });
    }
    setIsSelectOpen(false);
    setSelectField(null);
  }

  //전체선택 동기화
  useEffect(() => {
    if (filteredAndSortedData.length === 0) {
      setIsAllSelected(false);
      return;
    }

    const allIds = filteredAndSortedData.map((item) => item.idx);
    const isAll = allIds.length > 0 && allIds.every((idx) => selectedIds.includes(idx));

    setIsAllSelected(isAll);
  }, [filteredAndSortedData, selectedIds]);

  function handleItemClick(item) {
    if (selectedIds.length > 0) {
      return; // 선택 모드에서는 상세 진입 막기
    }

    onItemClick(item);
  }

  useEffect(() => {
    setSelectedIds([]);
    setIsAllSelected(false);
  }, [data]);

  function applySearchParams(values) {
    const apiParams = {};

    if (values.date1) apiParams.date1 = values.date1;
    if (values.date2) apiParams.date2 = values.date2;
    if (values.docType) apiParams.formCode = Number(values.docType);
    if (values.team) apiParams.teamCode = Number(values.team);
    if (values.title?.trim()) apiParams.title = values.title.trim();
    if (values.name?.trim()) apiParams.name = values.name.trim();

    const finalParams = cleanParams(apiParams);
    setSearchParams(finalParams);
    setIsFilterOpen(false);
  }

  return (
    <div className={styles.container}>
      {/* 리스트 섹션 */}
      <div className={styles.listSection}>
        {/* 검색 필드 */}
        <div className={styles.searchField}>
          <div className={styles.searchIcon}>
            <img src={searchIcon} alt="검색" width={19} height={19} />
          </div>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="제목을 입력해 주세요."
            value={searchValue}
            maxLength={200}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {searchValue && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={() => {
                setSearchValue('');
                setAppliedSearchValue('');
              }}
              aria-label="검색어 초기화"
            >
              <img src={closeIcon} alt="삭제" width={16} height={16} />
            </button>
          )}
          <button
            type="button"
            className={styles.settingButton}
            aria-label="필터"
            onClick={handleFilterClick}
          >
            <img src={searchFilterIcon} alt="필터" width={19} height={19} />
          </button>
        </div>

        {/* 정렬 드롭다운 */}
        <div className={styles.sortWrapperWithSelectAll} ref={sortDropdownRef}>
          {/* 전체 선택 */}
          <label className={styles.selectAll}>
            <input type="checkbox" checked={isAllSelected} onChange={handleToggleAll} />
            <span>전체 선택</span>
          </label>
          <button
            type="button"
            className={styles.sortButton}
            onClick={() => setIsSortOpen(!isSortOpen)}
          >
            <span className={styles.sortLabel}>{selectedSortLabel}</span>
            <img
              src={chevronDownIcon}
              alt=""
              width={16}
              height={16}
              className={`${styles.sortChevron} ${isSortOpen ? styles.sortChevronOpen : ''}`}
            />
          </button>

          {isSortOpen && (
            <ul className={styles.sortDropdown}>
              {sortOptions.map((option) => (
                <li key={option.value}>
                  <button
                    type="button"
                    className={`${styles.sortOption} ${sortValue === option.value ? styles.sortOptionActive : ''}`}
                    onClick={() => handleSortSelect(option.value)}
                  >
                    {option.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* 리스트 */}
        <div className={styles.listWrapper}>
          <MobileApprovalList
            items={filteredAndSortedData}
            onItemClick={handleItemClick}
            emptyMessage="참조 문서가 없습니다."
            selectable
            selectedIds={selectedIds}
            onSelectChange={setSelectedIds}
            getItemId={(item) => item.idx}
            clearSelectionTrigger={data} //체크박스 해제
            isLoading={isLoading}
            isFetching={isFetching}
          />
        </div>
      </div>
      <div className={styles.footer}>
        <button
          type="button"
          className={`${styles.actionButton} ${styles.primaryButton}`}
          onClick={() => onComplete(selectedIds)}
        >
          완료 상태 변경
        </button>
      </div>
      {/* 필터 바텀시트 */}
      <MobileFilterSheet
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        title="참조문서"
        filterFields={filterFields}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
        onApply={applySearchParams}
        onReset={handleFilterReset}
        onSelectFieldClick={handleSelectFieldClick}
      />

      {/* 셀렉트 풀스크린 */}
      <MobileSelectScreen
        isOpen={isSelectOpen}
        onClose={() => {
          setIsSelectOpen(false);
          setSelectField(null);
        }}
        title={selectField?.label || '선택'}
        options={selectField?.options || []}
        value={selectField?.currentValue}
        onChange={handleSelectChange}
      />
    </div>
  );
}

MobileReferenceContent.propTypes = {
  data: PropTypes.array,
  onItemClick: PropTypes.func,
  formOptions: PropTypes.array,
  teamOptions: PropTypes.array,
  onComplete: PropTypes.func,
};

MobileReferenceContent.defaultProps = {
  data: [],
  onItemClick: () => {},
  formOptions: [{ label: '전체', value: '' }],
  teamOptions: [{ label: '전체', value: '' }],
  onComplete: () => {},
};

export default MobileReferenceContent;
